import { useState, useEffect, useCallback } from "react";
import { apiFetch, ApiError } from "@/lib/api-client";

// ---------------------------------------------------------------------------
// Types — match api-crm serializer output (CompanySummaryDTO / CompanyDTO)
// ---------------------------------------------------------------------------

export interface CompanySummary {
  id: string;
  name: string;
  domain: string | null;
  industry: string | null;
  size: string | null;
  website: string | null;
  ownerId: string | null;
  createdAt: string;
}

export interface Company extends CompanySummary {
  address: Record<string, unknown> | null;
  annualRevenue: number | null;
  customFields: Record<string, unknown>;
  updatedAt: string;
}

export interface CompanyContactSummary {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  tags: string[];
  source: string | null;
  leadScore: number;
  engagementStatus: string;
  lifecycleStage: string;
  lastActivityAt: string | null;
  createdAt: string;
  role: string | null;
  isPrimary: boolean;
}

export interface CompanyDealSummary {
  id: string;
  pipelineId: string;
  stageId: string;
  personId: string | null;
  companyId: string | null;
  title: string;
  value: number | null;
  currency: string;
  expectedCloseDate: string | null;
  score: number;
  ownerId: string | null;
  createdAt: string;
}

export interface CompanyHierarchy {
  parent: CompanySummary | null;
  children: CompanySummary[];
}

export interface CompanyProfile {
  company: Company;
  metrics: {
    contactCount: number;
    openDealCount: number;
    totalDealValue: number;
  };
  contacts: CompanyContactSummary[];
  deals: CompanyDealSummary[];
  hierarchy: CompanyHierarchy;
}

interface CompanyListResponse {
  data: {
    items: CompanySummary[];
    nextCursor: string | null;
    total: number;
  };
}

// ---------------------------------------------------------------------------
// Hook: useCompanyList
// ---------------------------------------------------------------------------

interface UseCompanyListParams {
  search?: string;
  limit?: number;
}

export function useCompanyList(params: UseCompanyListParams) {
  const [companies, setCompanies] = useState<CompanySummary[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCompanies = useCallback(async (cursor?: string) => {
    setIsLoading(true);
    setError(null);

    const searchParams = new URLSearchParams();
    if (params.search) searchParams.set("search", params.search);
    if (params.limit) searchParams.set("limit", String(params.limit));
    if (cursor) searchParams.set("cursor", cursor);

    const qs = searchParams.toString();
    const path = `/crm/companies${qs ? `?${qs}` : ""}`;

    try {
      const res = await apiFetch<CompanyListResponse>(path);
      if (cursor) {
        setCompanies((prev) => [...prev, ...res.data.items]);
      } else {
        setCompanies(res.data.items);
      }
      setNextCursor(res.data.nextCursor);
      setTotal(res.data.total);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load companies");
    } finally {
      setIsLoading(false);
    }
  }, [params.search, params.limit]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const loadMore = useCallback(() => {
    if (nextCursor) fetchCompanies(nextCursor);
  }, [nextCursor, fetchCompanies]);

  return { companies, total, nextCursor, isLoading, error, loadMore, refetch: () => fetchCompanies() };
}

// ---------------------------------------------------------------------------
// Function: createCompany — POST /crm/companies
// ---------------------------------------------------------------------------

export interface CreateCompanyData {
  name: string;
  domain?: string;
  industry?: string;
  size?: string;
  website?: string;
  annualRevenue?: number;
  customFields?: Record<string, unknown>;
}

export async function createCompany(data: CreateCompanyData): Promise<Company> {
  const res = await apiFetch<{ data: Company }>("/crm/companies", {
    method: "POST",
    body: JSON.stringify(data),
  });
  return res.data;
}

export interface UpdateCompanyData {
  name?: string;
  domain?: string;
  industry?: string;
  size?: string;
  website?: string;
  annualRevenue?: number;
  ownerId?: string | null;
  customFields?: Record<string, unknown>;
}

export async function updateCompany(companyId: string, data: UpdateCompanyData): Promise<Company> {
  const res = await apiFetch<{ data: Company }>(`/crm/companies/${companyId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
  return res.data;
}

export async function deleteCompany(companyId: string): Promise<void> {
  await apiFetch<{ success: boolean }>(`/crm/companies/${companyId}`, {
    method: "DELETE",
  });
}

export async function restoreCompany(companyId: string): Promise<void> {
  await apiFetch<{ success: boolean }>(`/crm/companies/${companyId}/restore`, {
    method: "POST",
  });
}

export function useCompanyProfile(id: string | null) {
  const [profile, setProfile] = useState<CompanyProfile | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = useCallback(async () => {
    if (!id) {
      setProfile(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const res = await apiFetch<{ data: CompanyProfile }>(`/crm/companies/${id}/profile`);
      setProfile(res.data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load company");
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void fetchProfile();
  }, [fetchProfile]);

  return { profile, isLoading, error, refetch: fetchProfile };
}

export async function linkCompanyContact(
  companyId: string,
  input: { personId: string; role?: string; isPrimary?: boolean },
): Promise<CompanyProfile> {
  const res = await apiFetch<{ data: CompanyProfile }>(`/crm/companies/${companyId}/contacts`, {
    method: "POST",
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function unlinkCompanyContact(companyId: string, personId: string): Promise<CompanyProfile> {
  const res = await apiFetch<{ data: CompanyProfile }>(`/crm/companies/${companyId}/contacts/${personId}`, {
    method: "DELETE",
  });
  return res.data;
}

export async function linkCompanyDeal(companyId: string, dealId: string): Promise<CompanyProfile> {
  const res = await apiFetch<{ data: CompanyProfile }>(`/crm/companies/${companyId}/deals/${dealId}/link`, {
    method: "POST",
  });
  return res.data;
}

export async function unlinkCompanyDeal(companyId: string, dealId: string): Promise<CompanyProfile> {
  const res = await apiFetch<{ data: CompanyProfile }>(`/crm/companies/${companyId}/deals/${dealId}/unlink`, {
    method: "POST",
  });
  return res.data;
}

export async function setCompanyParent(
  companyId: string,
  parentCompanyId: string | null,
): Promise<CompanyProfile> {
  const res = await apiFetch<{ data: CompanyProfile }>(`/crm/companies/${companyId}/parent`, {
    method: "POST",
    body: JSON.stringify({ parentCompanyId }),
  });
  return res.data;
}
